/**
 * ?z=&c=&huc=&window= URL-state helpers, shared by the legacy Leaflet
 * path and the MapLibre tile-mode path (moved out of explore.tsx
 * unchanged in behavior, except the z floor drops 5 → 3 so
 * national-view deep links round-trip; Leaflet clamps to its own
 * minZoom on setView so legacy links are unaffected).
 *
 * Format:  ?z=12&c=38.9001,-77.0388&huc=020700100102&window=1y
 * All params optional. `z`/`c` describe the viewport; `huc` selects the
 * watershed that seeds the pill + Sources sheet at load; `window`
 * selects the time-window control (1y|90d — absent/anything else means
 * "all time", the default, which is never written to the URL).
 */

import type { TimeWindow } from './time-window';

export interface MapUrlState {
  zoom?: number;
  lat?: number;
  lon?: number;
  /** string = set ?huc=; null = explicitly remove ?huc=; undefined = leave alone. */
  huc12?: string | null;
  /** 'all' (or absent) never appears in the URL — it's the default. */
  window?: TimeWindow;
}

export function parseMapStateFromUrl(): MapUrlState {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const out: MapUrlState = {};
  const z = p.get('z');
  if (z !== null) {
    const n = parseInt(z, 10);
    // 3..19 covers CONUS overview through street zoom — anything outside
    // is a malformed link, ignore.
    if (Number.isFinite(n) && n >= 3 && n <= 19) out.zoom = n;
  }
  const c = p.get('c');
  if (c !== null) {
    const [latStr, lonStr] = c.split(',');
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (
      Number.isFinite(lat) && Number.isFinite(lon) &&
      lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
    ) {
      out.lat = lat;
      out.lon = lon;
    }
  }
  const huc = p.get('huc');
  // HUC-12s are always exactly 12 digits.
  if (huc !== null && /^[0-9]{12}$/.test(huc)) out.huc12 = huc;
  const w = p.get('window');
  if (w === '1y' || w === '90d') out.window = w;
  return out;
}

/**
 * Write z/c/huc into the URL without losing other query params (?debug,
 * ?next, future flags). `replaceState` rather than `pushState` so back-
 * button doesn't fill up with pan history. Caller should debounce — this
 * fires on every moveend/zoomend.
 *
 * Pass `huc12: null` (not undefined) to explicitly REMOVE the huc param
 * (e.g., when the map panned outside any loaded watershed).
 */
export function writeMapStateToUrl(state: MapUrlState): void {
  if (typeof window === 'undefined') return;
  const p = new URLSearchParams(window.location.search);
  if (state.zoom !== undefined) p.set('z', String(Math.round(state.zoom)));
  if (state.lat !== undefined && state.lon !== undefined) {
    // 4 decimals = ~11m precision. Enough for the map; keeps the URL short.
    p.set('c', `${state.lat.toFixed(4)},${state.lon.toFixed(4)}`);
  }
  if (state.huc12 === null) p.delete('huc');
  else if (state.huc12) p.set('huc', state.huc12);
  // 'all' is the default and is never written — keeps shared links short
  // and matches the "absent = all" contract.
  if (state.window === '1y' || state.window === '90d') p.set('window', state.window);
  else if (state.window === 'all') p.delete('window');
  const qs = p.toString();
  const next = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}
